// FLAGS: identity
// The scheduler sign-in page and its step machine: Google auth-code, then authenticator enrollment
// where the account has none, then the TOTP code.
import { useState } from "react";
import { CalendarDays } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@openup/shared/primitives/ui/card";
import { useCurrentUser } from "@src/queries/auth";
import isCustomerSuccess from "@src/auth/userRoles";
import LoginStepContent from "@src/pages/login/LoginStepContent";

export type LoginStep = "google" | "enroll" | "totp" | "registered";

/**
 * FLAGS: identity
 * The step order is local component state. Each factor is enforced independently by
 * the API, so reordering or skipping a step here cannot skip a factor — it only
 * strands the user on a screen the backend will not accept.
 * Own CustomerSuccess login: Google SSO (auth-code flow) followed by the
 * authenticator (TOTP) step that upgrades the token to satisfy
 * CustomerSuccessPolicy. Mirrors the dashboard's flow, Tailwind-only.
 */
const LoginPage = () => {
  const [step, setStep] = useState<LoginStep>("google");
  const { data: user } = useCurrentUser();

  // Authenticated (token validated) but missing the CustomerSuccess role —
  // PublicOnlyRoute only redirects CS users, so surface the denial here.
  const isNotPermitted = Boolean(user) && !isCustomerSuccess(user);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="flex justify-center">
            <CalendarDays className="h-10 w-10 text-green-700" aria-hidden />
          </div>
          <CardTitle>OpenUp Spaces</CardTitle>
          {!isNotPermitted && step === "google" && (
            <p className="text-sm text-gray-600">
              Sign in with your OpenUp Google account
            </p>
          )}
        </CardHeader>
        <CardContent>
          <LoginStepContent
            step={step}
            isNotPermitted={isNotPermitted}
            onStepChange={setStep}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
